import { Switch, Route, Redirect, useSearch } from "wouter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { toast } from "sonner";
import { ChatPage } from "@/main";
import { IdentityConnectPage } from "@/components/identity-connect";
import { Ms365OnboardingPage } from "@/components/ms365-onboarding-page";
import { Ms365CallbackPage } from "@/components/ms365-callback-page";
import { GoogleCallbackPage } from "@/components/google-callback-page";
import { ConnectionsPage } from "@/components/connections-page";
import { CostsPageRoute } from "@/components/costs-page";
import {
  AgentConfigurationPageRoute,
  TenantAdministrationPageRoute,
} from "@/components/admin-route-pages";
import { AgentViewPage } from "@/components/views/agent-view-page";
import { WorkOSCallbackPage } from "@/components/workos-callback-page";
import { WorkOSImpersonationBanner } from "@/components/workos-impersonation-banner";
import { BackendConnectivityBanner } from "@/components/backend-connectivity-banner";
import { Spinner } from "@/components/ui/spinner";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { useBackendConnectivity } from "@/hooks/use-backend-connectivity";
import { startWorkOSReauthentication } from "@/lib/workos-auth";
import { resetImpersonationCache } from "@/lib/ui-cache";
import {
  clearWorkOSAuthorizationMarker,
  getWorkOSRedirectUri,
} from "@/lib/workos-callback";

/**
 * Redirect the old flat /projects entry to the dedicated project catalog.
 *
 * Old query-based deep links (`/projects?project=<id>&task=<id>`) are rewritten
 * to the singular project workspace (`/project/<id>`) so bookmarks and external
 * links keep working while the ambiguous `/projects` workspace no longer
 * implicitly selects a project.
 */
function ProjectsLegacyRedirect() {
  const search = useSearch();
  const query = useMemo(() => new URLSearchParams(search), [search]);

  const to = useMemo(() => {
    if (query.get("new") === "1") {
      return "/project/new";
    }

    const projectId = query.get("project");
    if (projectId) {
      const next = new URLSearchParams();
      const taskId = query.get("task");
      if (taskId) next.set("task", taskId);
      const queryString = next.toString();
      return `/project/${encodeURIComponent(projectId)}${queryString ? `?${queryString}` : ""}`;
    }

    return "/projects/list";
  }, [query]);

  return <Redirect to={to} />;
}

function AppContent() {
  const { user, impersonator, isLoading, getAccessToken } = useAuthWithTestMode();
  const { t } = useTranslation();
  const cacheIdentity = `${user?.id ?? ""}:${Boolean(impersonator)}`;
  const [cacheReadyFor, setCacheReadyFor] = useState<string | null>(null);
  const cacheReady = cacheReadyFor === cacheIdentity;
  useEffect(() => {
    if (isLoading || !user) return;
    let active = true;
    setCacheReadyFor(null);
    void resetImpersonationCache(Boolean(impersonator)).catch(() => {
      if (active) toast.error(t("auth.cacheResetFailed"), {
        id: `cache-reset-failed:${cacheIdentity}`,
        duration: 10000,
      });
    }).finally(() => {
      if (active) setCacheReadyFor(cacheIdentity);
    });
    return () => { active = false; };
  }, [isLoading, cacheIdentity]);
  const connectivity = useBackendConnectivity(
    getAccessToken,
    !isLoading && Boolean(user),
  );
  const canRenderRoutes = (!isLoading && (!user || cacheReady))
    || window.location.pathname === "/auth/callback";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {!canRenderRoutes && (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <Spinner className="size-8 text-muted-foreground" />
        </div>
      )}
      {canRenderRoutes && <>
      <WorkOSImpersonationBanner />
      <BackendConnectivityBanner
        status={connectivity.status}
        onRetry={connectivity.retryNow}
      />
      <div className="min-h-0 flex-1 overflow-auto [&>*]:!h-full [&>*]:!min-h-0">
        <Switch>
          {/* WorkOS Hosted UI + Dashboard impersonation callback */}
          <Route path="/auth/callback" component={WorkOSCallbackPage} />

          {/* Identity connect — one-time link destination */}
          <Route path="/identity/connect" component={IdentityConnectPage} />

          {/* Standalone configured agent view */}
          <Route path="/agent/:agentId/views/:viewId" component={AgentViewPage} />

          {/* Standalone conversation-scoped pinned view */}
          <Route path="/session/:sessionId/views/:viewId" component={AgentViewPage} />

          {/* Agent conversations hub — uses the authenticated chat shell. */}
          <Route path="/agent/:agentId/conversations" component={ChatPage} />

          {/* Session view — with optional panel=file:… pop-out state */}
          <Route path="/session/:sessionId" component={ChatPage} />

          {/* Root — auto-select most recent session */}
          <Route path="/" component={ChatPage} />

          {/* MS365 onboarding + callback — standalone, no WorkOS auth required */}
          <Route path="/ms365/onboard" component={Ms365OnboardingPage} />
          <Route path="/ms365/callback" component={Ms365CallbackPage} />
          <Route path="/google/callback" component={GoogleCallbackPage} />

          {/* Connections dashboard — WorkOS-authenticated */}
          <Route path="/connections" component={ConnectionsPage} />

          {/* Usage and legacy cost bookmarks — WorkOS-authenticated */}
          <Route path="/usage" component={CostsPageRoute} />
          <Route path="/costs"><Redirect to="/usage" /></Route>

          {/* Administrative surfaces — WorkOS-authenticated and capability-gated. */}
          <Route path="/admin/tenants/:tenantId/:tab" component={TenantAdministrationPageRoute} />
          <Route path="/admin/tenants/:tenantId" component={TenantAdministrationPageRoute} />
          <Route path="/admin/tenants" component={TenantAdministrationPageRoute} />
          <Route path="/admin/agent-config/:tab" component={AgentConfigurationPageRoute} />
          <Route path="/admin/agent-config" component={AgentConfigurationPageRoute} />

          {/* Projects catalog — uses the same authenticated chat shell/sidebar. */}
          <Route path="/projects/list" component={ChatPage} />

          {/* Dedicated project workspace (create + project by id). */}
          <Route path="/project/new" component={ChatPage} />
          <Route path="/project/:projectId" component={ChatPage} />

          {/* Legacy /projects query links redirect to the new dedicated routes. */}
          <Route path="/projects" component={ProjectsLegacyRedirect} />

          {/* Legacy /projects/<id> path guard — never render a project here. */}
          <Route path="/projects/:id">
            {({ id }) => <Redirect to={`/project/${encodeURIComponent(id)}`} />}
          </Route>

          {/* Catch-all → redirect to root */}
          <Route>
            <Redirect to="/" />
          </Route>
        </Switch>
      </div>
      </>}
    </div>
  );
}

export function App() {
  return (
    <AuthKitProvider
      // Browser tokens go to same-origin chat-bridge, which gates Agno traffic.
      clientId={import.meta.env.WORKOS_CLIENT_ID ?? ""}
      apiHostname={window.location.host}
      redirectUri={getWorkOSRedirectUri()}
      // Cookie-based auth (devMode=false) requires the proxy to maintain a
      // cookie jar for api.workos.com, which our simple proxy doesn\'t do.
      // Use localStorage-based tokens everywhere for now.
      devMode={true}
      // If a forced token refresh fails, AuthKit clears the unusable session.
      // Begin a normal WorkOS sign-in rather than leaving the UI displaying a
      // stale user whose API calls can no longer authenticate.
      onRefreshFailure={({ signIn }) => {
        startWorkOSReauthentication(signIn);
      }}
      onRefresh={() => {
        clearWorkOSAuthorizationMarker();
      }}
    >
      <AppContent />
    </AuthKitProvider>
  );
}
